/**
 * leadRouter.ts â typed API client for Lead Auto-Router dashboard
 * Wave 1 (read) + Wave 2 (edit) endpoints.
 * Mounted at /api/v1/admin/lead-router/...
 */

import { api } from '@/utils/api';

// âââ Shared primitives ââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

export interface RouterStaff {
  id: string;
  name: string;
  email: string;
  roleName: string | null;
  reportsToId: string | null;
}

export interface RouterStaffGraph {
  roleByKey: Record<string, string>;
  /** reportsTo-based execâmanager (legacy, INCOMPLETE â see teamManagerByUser). */
  managerByExec: Record<string, string>;
  execByManager: Record<string, string[]>;
  /**
   * STRUCTURAL execâteam-manager map (Team.managerId + primaryTeam/TeamMembership) â
   * the SAME source decideRouteV2 escalates through. Preferred over managerByExec
   * for escalation decisions (Bug-3 fix, 2026-07-23). Optional for back-compat with
   * older payloads.
   */
  teamManagerByUser?: Record<string, string>;
}

export interface CategoryResolutionRule {
  id: string;
  tenantId: string;
  capturedCategory: string;
  applicationType: string | null;
  routingGroup: string;
  rank: number;
}

export interface TerritoryRule {
  id: string;
  tenantId: string;
  productGroup: string;
  matchType: 'city' | 'state' | 'catchall';
  matchValue: string;
  ownerUserId: string | null;
  dealerId: string | null;
  rank: number;
}

export interface RouterDealer {
  id: string;
  name: string;
  waGroupId: string | null;
  followUpOwnerId: string | null;
  managerUserId: string | null;
}

export interface RouterSettings {
  autoRouterEnabled: boolean;
  dealerWhatsAppSendEnabled: boolean;
  fallbackAction: string;
}

// âââ Coverage response ââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

export interface CoverageData {
  settings: RouterSettings;
  staff: RouterStaff[];
  staffGraph: RouterStaffGraph;
  categoryRules: CategoryResolutionRule[];
  territoryRules: TerritoryRule[];
  cityDistrictMap: Record<string, string>;
  dealers: RouterDealer[];
}

// âââ Simulate âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

export interface SimulateInput {
  capturedCategory?: string | null;
  applicationType?: string | null;
  location?: string | null;
  locationState?: string | null;
  interestLevel?: string | null;
  isB2bPriority?: boolean | null;
}

export interface SimulateResult {
  assignee: { userId: string; name: string | null } | null;
  dealerId: string | null;
  matchedStage: string | null;
  action: string;
  temperature: string | null;
  trace: string[];
  decidedBy: 'overlay' | '4-stage';
  overlayRuleId: string | null;
}

// âââ Overlay rules ââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

// --- Simulate v2 -------------------------------------------------------------
// ADDED ALONGSIDE the v1 SimulateInput/SimulateResult above -- v1 stays live for
// LeadRouterPage's graph highlighting and LeadRoutingPage.test.tsx. v2 is a
// separate endpoint (not a version flag) because the request renames 3 fields,
// adds 4, and returns a structurally different trace. See SIMULATE-V2-CONTRACT S1.

export interface SimulateV2Input {
  capturedCategory?: string | null;
  applicationType?: string | null;
  city?: string | null;
  district?: string | null;
  state?: string | null;
  /** 6 digits, or null. */
  pincode?: string | null;
  /** false -> the rank-5 pincode tier is skipped entirely. */
  pincodeConfident?: boolean;
  interestLevel?: string | null;
  isB2B?: boolean;
  humanTouched?: boolean;
  /** Bypass category resolution and test a territory grid directly. */
  productGroupOverride?: string | null;
  /** Optional assertion -- turns a run into a pass/fail check. */
  expected?: {
    ownerUserId?: string | null;
    dealerId?: string | null;
    destination?: 'user' | 'dealer_dispatch' | 'triage' | null;
  } | null;
}

export type SimulateV2Destination = 'user' | 'dealer_dispatch' | 'triage';

export interface SimulateV2Candidate {
  layer: RouteLayerV2;
  userId: string | null;
  dealerId: string | null;
  /** Enriched by the route -- user full name or dealer name; null if unresolvable. */
  name: string | null;
  matchType: string;
  matchValue: string;
  rank: number;
  priority: number;
  primary: boolean;
  chosen: boolean;
  rejectReason: string | null;
}

export interface SimulateV2ResolvedLead {
  capturedCategory: string | null;
  applicationType: string | null;
  city: string | null;
  district: string | null;
  state: string | null;
  pincode: string | null;
  pincodeConfident: boolean;
  interestLevel: string | null;
  isB2B: boolean;
  humanTouched: boolean;
}

export interface SimulateV2Assertion {
  pass: boolean;
  expected: { ownerUserId?: string | null; dealerId?: string | null; destination?: string | null };
  actual: { ownerUserId: string | null; dealerId: string | null; destination: string };
  mismatches: string[];
}

export interface SimulateV2Result {
  destination: SimulateV2Destination;
  ownerUserId: string | null;
  ownerName: string | null;
  assignedDealerId: string | null;
  dealerName: string | null;
  handoffToManager: boolean;
  notifyOnly: boolean;
  triageReason: string | null;
  trace: {
    group: string | null;
    tier: 'HOT' | 'WARM' | 'COLD' | null;
    layerRun: RouteLayerV2[];
    /** Winner + EVERY rejected candidate. */
    candidates: SimulateV2Candidate[];
    toggleState: { escalationEnabled: boolean; autoDealerRouting: boolean };
    lines: string[];
  };
  /** Exactly what was fed to decideRouteV2 after normalisation + pincode derivation. */
  resolvedLead: SimulateV2ResolvedLead;
  /** Present ONLY when the request carried `expected`. */
  assertion: SimulateV2Assertion | null;
  notes: string[];
}

export type SimulateV2BatchLead = SimulateV2Input & { label?: string | null };

export interface SimulateV2BatchRow {
  index: number;
  label: string | null;
  destination: SimulateV2Destination;
  ownerUserId: string | null;
  ownerName: string | null;
  assignedDealerId: string | null;
  dealerName: string | null;
  group: string | null;
  tier: string | null;
  /** The WINNING candidate's matchType, else null. */
  matchType: string | null;
  matchValue: string | null;
  handoffToManager: boolean;
  notifyOnly: boolean;
  triageReason: string | null;
  assertion: { pass: boolean; mismatches: string[] } | null;
}

export interface SimulateV2BatchResult {
  results: SimulateV2BatchRow[];
  summary: {
    total: number;
    byDestination: { user: number; dealer_dispatch: number; triage: number };
    asserted: number;
    passed: number;
    failed: number;
  };
}

/** A row of the GLOBAL PincodeArea table. There is NO city column. */
export interface GeoPincode {
  pincode: string;
  district: string;
  state: string;
}

export interface OverlayCondition {
  op: 'AND' | 'OR';
  items: Array<{
    field: string;
    op: string;
    value: string;
  }>;
}

export interface OverlayTarget {
  action: string;
  ownerUserId?: string | null;
  dealerId?: string | null;
}

export interface OverlayRule {
  id: string;
  tenantId: string;
  priority: number;
  name: string;
  conditions: OverlayCondition;
  target: OverlayTarget;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// âââ API client âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

export const leadRouterApi = {
  // ââ Wave 1 ââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ
  getCoverage(): Promise<CoverageData> {
    return api
      .get<{ success: true; data: CoverageData }>('/admin/lead-router/coverage')
      .then((r) => r.data.data);
  },

  simulate(input: SimulateInput): Promise<SimulateResult> {
    return api
      .post<{ success: true; data: SimulateResult }>('/admin/lead-router/simulate', input)
      .then((r) => r.data.data);
  },

  // ââ Settings âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ
  updateSettings(patch: {
    autoRouterEnabled?: boolean;
    dealerWhatsAppSendEnabled?: boolean;
  }): Promise<{ autoRouterEnabled: boolean; dealerWhatsAppSendEnabled: boolean }> {
    return api
      .put<{
        success: true;
        data: { autoRouterEnabled: boolean; dealerWhatsAppSendEnabled: boolean };
      }>('/admin/lead-router/settings', patch)
      .then((r) => r.data.data);
  },

  // ââ CategoryResolutionRule ââââââââââââââââââââââââââââââââââââââââââââââââââââ
  createCategoryRule(body: {
    capturedCategory: string;
    applicationType?: string | null;
    routingGroup: string;
    rank?: number;
  }): Promise<CategoryResolutionRule> {
    return api
      .post<{ success: true; data: CategoryResolutionRule }>(
        '/admin/lead-router/category-rules',
        body,
      )
      .then((r) => r.data.data);
  },

  patchCategoryRule(
    id: string,
    body: Partial<{
      capturedCategory: string;
      applicationType: string | null;
      routingGroup: string;
      rank: number;
    }>,
  ): Promise<CategoryResolutionRule> {
    return api
      .patch<{ success: true; data: CategoryResolutionRule }>(
        `/admin/lead-router/category-rules/${id}`,
        body,
      )
      .then((r) => r.data.data);
  },

  deleteCategoryRule(id: string): Promise<void> {
    return api.delete(`/admin/lead-router/category-rules/${id}`).then(() => undefined);
  },

  // ââ TerritoryRule âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ
  createTerritoryRule(body: {
    productGroup: string;
    matchType: 'city' | 'state' | 'catchall';
    matchValue: string;
    ownerUserId?: string | null;
    dealerId?: string | null;
    rank?: number;
  }): Promise<TerritoryRule> {
    return api
      .post<{ success: true; data: TerritoryRule }>(
        '/admin/lead-router/territory-rules',
        body,
      )
      .then((r) => r.data.data);
  },

  patchTerritoryRule(
    id: string,
    body: Partial<{
      productGroup: string;
      matchType: 'city' | 'state' | 'catchall';
      matchValue: string;
      ownerUserId: string | null;
      dealerId: string | null;
      rank: number;
    }>,
  ): Promise<TerritoryRule> {
    return api
      .patch<{ success: true; data: TerritoryRule }>(
        `/admin/lead-router/territory-rules/${id}`,
        body,
      )
      .then((r) => r.data.data);
  },

  deleteTerritoryRule(id: string): Promise<void> {
    return api.delete(`/admin/lead-router/territory-rules/${id}`).then(() => undefined);
  },

  // ââ Dealers âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ
  createDealer(body: {
    name: string;
    waGroupId?: string | null;
    followUpOwnerId?: string | null;
    productGroups?: string[];
    phone?: string | null;
    email?: string | null;
    gstin?: string | null;
  }): Promise<RouterDealer> {
    return api
      .post<{ success: true; data: RouterDealer }>('/admin/lead-router/dealers', body)
      .then((r) => r.data.data);
  },

  patchDealer(
    id: string,
    body: Partial<{
      name: string;
      waGroupId: string | null;
      followUpOwnerId: string | null;
      productGroups: string[];
      phone: string | null;
      email: string | null;
      gstin: string | null;
      isActive: boolean;
      managerUserId: string | null;
    }>,
  ): Promise<RouterDealer> {
    return api
      .patch<{ success: true; data: RouterDealer }>(`/admin/lead-router/dealers/${id}`, body)
      .then((r) => r.data.data);
  },

  deactivateDealer(id: string): Promise<void> {
    return api.delete(`/admin/lead-router/dealers/${id}`).then(() => undefined);
  },

  // ââ Overlay rules âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ
  listOverlayRules(): Promise<OverlayRule[]> {
    return api
      .get<{ success: true; data: OverlayRule[] }>('/admin/lead-router/overlay-rules')
      .then((r) => r.data.data);
  },

  createOverlayRule(body: {
    priority: number;
    name: string;
    conditions: OverlayCondition;
    target: OverlayTarget;
    enabled?: boolean;
  }): Promise<OverlayRule> {
    return api
      .post<{ success: true; data: OverlayRule }>('/admin/lead-router/overlay-rules', body)
      .then((r) => r.data.data);
  },

  patchOverlayRule(
    id: string,
    body: Partial<{
      priority: number;
      name: string;
      conditions: OverlayCondition;
      target: OverlayTarget;
      enabled: boolean;
    }>,
  ): Promise<OverlayRule> {
    return api
      .patch<{ success: true; data: OverlayRule }>(
        `/admin/lead-router/overlay-rules/${id}`,
        body,
      )
      .then((r) => r.data.data);
  },

  deleteOverlayRule(id: string): Promise<void> {
    return api.delete(`/admin/lead-router/overlay-rules/${id}`).then(() => undefined);
  },

  reorderOverlayRules(rules: Array<{ id: string; priority: number }>): Promise<OverlayRule[]> {
    return api
      .post<{ success: true; data: OverlayRule[] }>('/admin/lead-router/overlay-rules/reorder', {
        rules,
      })
      .then((r) => r.data.data);
  },
};

// âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ
// Lead Router v2 (Wave 3 / S6) â coverage matrix (M1), contender, authoring API
// âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

export type RouteLayerV2 = 'EXEC' | 'MANAGER' | 'DEALER';
export type MatchTypeV2 = 'pincode' | 'city' | 'district' | 'state' | 'catchall';
export type CellDestination = 'human' | 'dealer' | 'triage';

export interface CoverageCellV2 {
  productGroupKey: string;
  state: string;
  owned: boolean;
  gap: boolean;
  execOverlap: boolean;
  managerOverlap: boolean;
  dealer: boolean;
  destination: CellDestination;
}

export interface CoverageMatrixV2 {
  groups: string[];
  states: string[];
  cells: CoverageCellV2[];
  totals: {
    owned: number; gap: number; execOverlap: number; managerOverlap: number;
    dealer: number; human: number; triage: number;
  };
}

export interface RoutingAssignmentV2 {
  id: string;
  tenantId: string;
  layer: RouteLayerV2;
  userId: string | null;
  dealerId: string | null;
  productGroupId: string | null;
  productGroupKey: string | null;
  matchType: MatchTypeV2;
  matchValue: string;
  deny: boolean;
  primary: boolean;
  rank: number;
  active: boolean;
}

export interface RoutingProductGroupV2 {
  id: string;
  tenantId: string;
  key: string;
  label: string;
  sortOrder: number;
  temperatureLadderEnabled: boolean;
  active: boolean;
}

export interface EscalationConfigV2 {
  id: string;
  tenantId: string;
  managerUserId: string;
  productGroupKey: string;
  enabled: boolean;
}

/** LOG5 — COLD → triage de-escalation store row. Same shape as escalation but
 *  default-OFF (no row → no de-escalation). */
export interface DeescalationConfigV2 {
  id: string;
  tenantId: string;
  managerUserId: string;
  productGroupKey: string;
  enabled: boolean;
}

export interface AssignmentInputV2 {
  layer: RouteLayerV2;
  userId?: string | null;
  dealerId?: string | null;
  productGroupKey?: string | null;
  matchType: MatchTypeV2;
  matchValue: string;
  deny?: boolean;
  primary?: boolean;
  rank?: number;
}

export interface CoverageGapV2 { productGroupKey: string; matchType: string; matchValue: string }
export interface CoverageWarningV2 {
  kind: 'exec_overlap_needs_primary' | 'manager_overlap_to_triage';
  productGroupKey: string; matchType: string; matchValue: string; ownerCount: number;
}

export interface BulkSaveResult {
  created: RoutingAssignmentV2[];
  deactivated: string[];
  warnings: CoverageWarningV2[];
  gaps: CoverageGapV2[];
}

/** Thrown-shape helper: the 422 Safety-#1 reject body. */
export interface BulkRejectBody {
  success: false;
  error: string;
  gaps: CoverageGapV2[];
  warnings: CoverageWarningV2[];
}

// âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ
// Coverage-Matrix Wave 2 (drill + territory panel) â binds Squad CE's locked
// read endpoints. Shapes mirror backend coverage-read.service.ts exactly.
// âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

/** Completeness color from the pure engine: fully / partial-or-broad / none. */
export type CoverageColorV2 = 'green' | 'yellow' | 'red';
export type MatrixTone = CoverageColorV2; // UI-B (Dealer-Territory tab) alias â same values

export interface CompletenessCellV2 {
  productGroupKey: string;
  state: string;                       // canonical display name
  cell: CoverageColorV2;
  ownedSub: number;
  totalSub: number;
  broadOnly: boolean;                  // covered only by a state/catch-all rule
  overlap: { exec: boolean; manager: boolean };
}

export interface CoverageMatrixV2Response {
  layer: 'EXEC' | 'DEALER' | 'PLUMBER' | 'ARCHITECT' | 'AFFILIATE';
  groups: string[];
  states: string[];                    // all 36 canonical names
  cells: CompletenessCellV2[];
}

export interface CoverageDrillCellV2 {
  unit: string;                        // district name (level=district) or pincode (level=pincode)
  cell: CoverageColorV2;
  ownedSub: number;
  totalSub: number;
  broadOnly: boolean;
  overlap: { exec: boolean; manager: boolean };
}

export interface CoverageDrillV2 {
  layer: 'EXEC' | 'DEALER' | 'PLUMBER' | 'ARCHITECT' | 'AFFILIATE';
  productGroupKey: string;
  state: string;
  district: string | null;            // null â drilled to districts; set â drilled to pincodes
  level: 'district' | 'pincode';
  cells: CoverageDrillCellV2[];
}

export interface TerritoryDetailV2 {
  productGroupKey: string;
  matchType: MatchTypeV2;
  matchValue: string;
  execs: Array<{ id: string; name: string }>;
  managers: Array<{ id: string; name: string; escalationOn: boolean }>;
  dealers: Array<{ id: string; name: string }>;
  hasExec: boolean;
}

export type CoverageLayerParam = 'EXEC' | 'DEALER' | 'PLUMBER' | 'ARCHITECT' | 'AFFILIATE'; // SQ3 ③.4
export const leadRouterV2Api = {
  getCoverageMatrix(): Promise<CoverageMatrixV2> {
    return api
      .get<{ success: true; data: CoverageMatrixV2 }>('/admin/lead-router/coverage-matrix')
      .then((r) => r.data.data);
  },

  getContender(leadId: string): Promise<any> {
    return api
      .get<{ success: true; data: any }>(`/admin/lead-router/contender/${leadId}`)
      .then((r) => r.data.data);
  },

  listAssignments(filter?: {
    userId?: string; dealerId?: string; layer?: RouteLayerV2; group?: string; active?: boolean;
  }): Promise<RoutingAssignmentV2[]> {
    const qs = new URLSearchParams();
    if (filter?.userId) qs.set('userId', filter.userId);
    if (filter?.dealerId) qs.set('dealerId', filter.dealerId);
    if (filter?.layer) qs.set('layer', filter.layer);
    if (filter?.group) qs.set('group', filter.group);
    if (filter?.active !== undefined) qs.set('active', String(filter.active));
    const q = qs.toString();
    return api
      .get<{ success: true; data: RoutingAssignmentV2[] }>(`/admin/lead-router/assignments${q ? `?${q}` : ''}`)
      .then((r) => r.data.data);
  },

  /** Wizard save. On a Safety-#1 reject the API returns 422 â caller catches. */
  bulkSaveAssignments(body: {
    assignments: AssignmentInputV2[];
    deactivateIds?: string[];
    fallbackUserId?: string | null;
  }): Promise<BulkSaveResult> {
    return api
      .post<{ success: true; data: BulkSaveResult }>('/admin/lead-router/assignments/bulk', body)
      .then((r) => r.data.data);
  },

  patchAssignment(
    id: string,
    body: Partial<{ rank: number; primary: boolean; active: boolean; deny: boolean; matchValue: string }>,
  ): Promise<RoutingAssignmentV2> {
    return api
      .patch<{ success: true; data: RoutingAssignmentV2 }>(`/admin/lead-router/assignments/${id}`, body)
      .then((r) => r.data.data);
  },

  deleteAssignment(id: string): Promise<void> {
    return api.delete(`/admin/lead-router/assignments/${id}`).then(() => undefined);
  },

  listProductGroups(): Promise<RoutingProductGroupV2[]> {
    return api
      .get<{ success: true; data: RoutingProductGroupV2[] }>('/admin/lead-router/product-groups')
      .then((r) => r.data.data);
  },

  getEscalation(managerUserId?: string): Promise<EscalationConfigV2[]> {
    const q = managerUserId ? `?managerUserId=${encodeURIComponent(managerUserId)}` : '';
    return api
      .get<{ success: true; data: EscalationConfigV2[] }>(`/admin/lead-router/escalation${q}`)
      .then((r) => r.data.data);
  },

  putEscalation(
    body:
      | { managerUserId: string; productGroupKey: string; enabled: boolean }
      | { configs: Array<{ managerUserId: string; productGroupKey: string; enabled: boolean }> },
  ): Promise<EscalationConfigV2[]> {
    return api
      .put<{ success: true; data: EscalationConfigV2[] }>('/admin/lead-router/escalation', body)
      .then((r) => r.data.data);
  },

  // LOG5 — COLD → triage de-escalation store (mirror of escalation, default-OFF).
  getDeescalation(managerUserId?: string): Promise<DeescalationConfigV2[]> {
    const q = managerUserId ? `?managerUserId=${encodeURIComponent(managerUserId)}` : '';
    return api
      .get<{ success: true; data: DeescalationConfigV2[] }>(`/admin/lead-router/deescalation${q}`)
      .then((r) => r.data.data);
  },

  putDeescalation(
    body:
      | { managerUserId: string; productGroupKey: string; enabled: boolean }
      | { configs: Array<{ managerUserId: string; productGroupKey: string; enabled: boolean }> },
  ): Promise<DeescalationConfigV2[]> {
    return api
      .put<{ success: true; data: DeescalationConfigV2[] }>('/admin/lead-router/deescalation', body)
      .then((r) => r.data.data);
  },

  /** S9 geo endpoint — "+100 km from X" district expansion (consumed by the M4c wizard). */
  expandRadius(body: { center: string; km: number; state?: string }): Promise<{ districts: Array<{ state: string; district: string }> }> {
    return api
      .post<{ success: true; data: { districts: Array<{ state: string; district: string }> } }>(
        '/admin/lead-router/expand-radius',
        body,
      )
      .then((r) => r.data.data);
  },

  // ââ Coverage-Matrix Wave 2 (drill + territory panel) ââââââââââââââââââââââââ
  /** All 36 states Ã product groups, completeness-colored. layer=EXEC default. */
  getCoverageMatrixV2(layer: CoverageLayerParam = 'EXEC'): Promise<CoverageMatrixV2Response> {
    return api
      .get<{ success: true; data: CoverageMatrixV2Response }>(
        `/admin/lead-router/coverage-matrix-v2?layer=${layer}&level=state`,
      )
      .then((r) => r.data.data);
  },

  /** Drill a state â districts, or a district â pincodes (pass `district`). */
  getCoverageDrill(params: {
    productGroupKey: string; state: string; district?: string | null; layer?: CoverageLayerParam;
  }): Promise<CoverageDrillV2> {
    const qs = new URLSearchParams();
    qs.set('layer', params.layer ?? 'EXEC');
    qs.set('productGroupKey', params.productGroupKey);
    qs.set('state', params.state);
    if (params.district) qs.set('district', params.district);
    return api
      .get<{ success: true; data: CoverageDrillV2 }>(`/admin/lead-router/coverage-drill?${qs.toString()}`)
      .then((r) => r.data.data);
  },

  /** Execs / managers / dealers assigned to a (productGroup, matchType, matchValue). */
  getTerritoryDetail(params: {
    productGroupKey: string; matchType: MatchTypeV2; matchValue: string;
  }): Promise<TerritoryDetailV2> {
    const qs = new URLSearchParams({
      productGroupKey: params.productGroupKey,
      matchType: params.matchType,
      matchValue: params.matchValue,
    });
    return api
      .get<{ success: true; data: TerritoryDetailV2 }>(`/admin/lead-router/territory-detail?${qs.toString()}`)
      .then((r) => r.data.data);
  },

  // ââ UI-B (Dealer-Territory tab) method names â thin aliases + dealer matrix (Wave-2 merge dedupe) ââ
  coverageDrill(params: { layer: 'EXEC' | 'DEALER'; productGroupKey: string; state: string; district?: string }): Promise<CoverageDrillV2> {
    return leadRouterV2Api.getCoverageDrill(params);
  },
  territoryDetail(params: { productGroupKey: string; matchType: MatchTypeV2; matchValue: string }): Promise<TerritoryDetailV2> {
    return leadRouterV2Api.getTerritoryDetail(params);
  },
  /** Dealer-territory completeness matrix (same shape, layer=DEALER). */
  dealerTerritoryMatrix(_level: 'state' = 'state'): Promise<CoverageMatrixV2Response> {
    return api
      .get<{ success: true; data: CoverageMatrixV2Response }>('/admin/lead-router/dealer-territory-matrix?level=state')
      .then((r) => r.data.data);
  },

  // -- Simulate v2 + geo lookups (READ-ONLY; never writes a routing decision) --

  /** Runs the v2 resolver against RoutingAssignment. Read-only. */
  simulateV2(input: SimulateV2Input): Promise<SimulateV2Result> {
    return api
      .post<{ success: true; data: SimulateV2Result }>('/admin/lead-router/simulate-v2', input)
      .then((r) => r.data.data);
  },

  /** 1..200 leads, one deps hydration for the whole grid. Read-only. */
  simulateV2Batch(leads: SimulateV2BatchLead[]): Promise<SimulateV2BatchResult> {
    return api
      .post<{ success: true; data: SimulateV2BatchResult }>(
        '/admin/lead-router/simulate-v2/batch',
        { leads },
      )
      .then((r) => r.data.data);
  },

  geoStates(): Promise<string[]> {
    return api
      .get<{ success: true; data: { states: string[] } }>('/admin/lead-router/geo/states')
      .then((r) => r.data.data.states);
  },

  geoDistricts(state: string): Promise<string[]> {
    return api
      .get<{ success: true; data: { state: string; districts: string[] } }>(
        `/admin/lead-router/geo/districts?state=${encodeURIComponent(state)}`,
      )
      .then((r) => r.data.data.districts);
  },

  geoPincodes(q: string, limit = 20): Promise<GeoPincode[]> {
    return api
      .get<{ success: true; data: { pincodes: GeoPincode[] } }>(
        `/admin/lead-router/geo/pincodes?q=${encodeURIComponent(q)}&limit=${limit}`,
      )
      .then((r) => r.data.data.pincodes);
  },

  geoPincode(pincode: string): Promise<GeoPincode> {
    return api
      .get<{ success: true; data: GeoPincode }>(
        `/admin/lead-router/geo/pincode/${encodeURIComponent(pincode)}`,
      )
      .then((r) => r.data.data);
  },
};
